import { getCandidateRole } from "./candidateRole";

// Hand-built boolean query parser for Keyword Search (role/skills/location/seniority/name).
// Grammar: expr := orExpr ; orExpr := andExpr (OR andExpr)* ; andExpr := term ((AND term) | term)*
// term := QUOTED_STRING | WORD | "(" expr ")"
// AND/OR are reserved words unless quoted (quote a phrase to search for the literal word).
// Adjacent bare terms with no explicit operator between them (e.g. `Entry level`)
// combine as an implicit AND, same as `Entry AND level`. A quoted phrase is always
// tokenized as a single TERM, so this never splits text inside quotes.

const QUOTE_ERROR = "Unmatched quote in search query.";
const PAREN_ERROR = "Unmatched parenthesis in search query.";
const SYNTAX_ERROR = "Invalid search query syntax.";

const queryError = (message) => {
  const err = new Error(message);
  err.isQueryError = true;
  return err;
};

const tokenize = (input) => {
  const tokens = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === '"') {
      const close = input.indexOf('"', i + 1);
      if (close === -1) throw queryError(QUOTE_ERROR);
      tokens.push({ type: "TERM", value: input.slice(i + 1, close), quoted: true });
      i = close + 1;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "LPAREN" });
      i++;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "RPAREN" });
      i++;
      continue;
    }
    const start = i;
    while (i < input.length && !/\s/.test(input[i]) && input[i] !== "(" && input[i] !== ")" && input[i] !== '"') {
      i++;
    }
    const word = input.slice(start, i);
    const upper = word.toUpperCase();
    if (upper === "AND") tokens.push({ type: "AND" });
    else if (upper === "OR") tokens.push({ type: "OR" });
    else tokens.push({ type: "TERM", value: word, quoted: false });
  }
  return tokens;
};

const parseTokens = (tokens) => {
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = () => tokens[pos++];

  const parseTerm = () => {
    const tok = peek();
    if (!tok) throw queryError(SYNTAX_ERROR);
    if (tok.type === "LPAREN") {
      consume();
      const node = parseOr();
      if (!peek() || peek().type !== "RPAREN") throw queryError(PAREN_ERROR);
      consume();
      return node;
    }
    if (tok.type === "TERM") {
      consume();
      return { type: "TERM", value: tok.value, quoted: tok.quoted };
    }
    throw queryError(SYNTAX_ERROR);
  };

  // Bare terms/groups placed adjacently with no explicit operator combine as an
  // implicit AND (e.g. `Entry level` behaves like `Entry AND level`). This only
  // ever fires between separate tokens the tokenizer already produced, so a
  // quoted phrase - always a single TERM token - is never split by it.
  const startsTerm = (tok) => Boolean(tok) && (tok.type === "TERM" || tok.type === "LPAREN");

  const parseAnd = () => {
    let node = parseTerm();
    while (peek()) {
      if (peek().type === "AND") {
        consume();
        node = { type: "AND", left: node, right: parseTerm() };
      } else if (startsTerm(peek())) {
        node = { type: "AND", left: node, right: parseTerm() };
      } else {
        break;
      }
    }
    return node;
  };

  const parseOr = () => {
    let node = parseAnd();
    while (peek() && peek().type === "OR") {
      consume();
      node = { type: "OR", left: node, right: parseAnd() };
    }
    return node;
  };

  if (!tokens.length) return null;
  const ast = parseOr();
  if (pos < tokens.length) {
    throw queryError(peek().type === "RPAREN" ? PAREN_ERROR : SYNTAX_ERROR);
  }
  return ast;
};

// Parses a raw query string into an AST. Never throws — returns { ast: null, error }
// on malformed input, and { ast: null, error: null } for an empty/whitespace query.
export const parseBooleanQuery = (rawQuery) => {
  const input = (rawQuery || "").trim();
  if (!input) return { ast: null, error: null };
  try {
    return { ast: parseTokens(tokenize(input)), error: null };
  } catch (err) {
    return { ast: null, error: err.isQueryError ? err.message : SYNTAX_ERROR };
  }
};

// Searchable fields: role, secondary roles, skills, seniority, city, country,
// and person's name. CV content is explicitly out of scope
// (see specs/keyword-search-boolean-query-parser.md).
const getSearchableValues = (candidate) => {
  const firstName = candidate?.user?.firstName || "";
  const lastName = candidate?.user?.lastName || "";
  const values = [
    getCandidateRole(candidate),
    ...(Array.isArray(candidate?.secondaryRoles) ? candidate.secondaryRoles : []),
    ...(Array.isArray(candidate?.skills) ? candidate.skills : []),
    candidate?.seniority,
    candidate?.user?.city,
    candidate?.user?.country,
    firstName,
    lastName,
    firstName && lastName ? `${firstName} ${lastName}` : null,
    firstName && lastName ? `${lastName} ${firstName}` : null,
  ];
  return values.filter(Boolean).map((v) => String(v).toLowerCase());
};

// Each atomic term (quoted or not) matches if it's a substring of ANY searchable
// field (OR across fields), case-insensitive.
export const evaluateBooleanQuery = (node, candidate) => {
  if (!node) return true;
  const values = getSearchableValues(candidate);
  const evalNode = (n) => {
    switch (n.type) {
      case "TERM":
        return values.some((v) => v.includes(n.value.toLowerCase()));
      case "AND":
        return evalNode(n.left) && evalNode(n.right);
      case "OR":
        return evalNode(n.left) || evalNode(n.right);
      default:
        return false;
    }
  };
  return evalNode(node);
};
